/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Soft UI Dashboard PRO Material.
 * You can add new color using this file.
 * You can customized the colors for the entire Soft UI Dashboard PRO Material using thie file.
 */

const lightColors = {
  background: {
    default: "#f8f9fa",
    paper: "#ffffff",
  },

  text: {
    main: "#67748e",
    focus: "#67748e",
    primary: "#344767",
    secondary: "#67748e",
    disabled: "#a0aec0",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#141414",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#00bfa5",
    focus: "#1de9b6",
  },

  secondary: {
    main: "#8392ab",
    focus: "#96a2b8",
  },

  info: {
    main: "#00bfa5",
    focus: "#1de9b6",
  },

  success: {
    main: "#82d616",
    focus: "#95dc39",
  },

  warning: {
    main: "#fbcf33",
    focus: "#fcd652",
  },

  error: {
    main: "#ea0606",
    focus: "#c70505",
  },

  light: {
    main: "#e9ecef",
  },

  dark: {
    main: "#344767",
    focus: "#65799c",
  },

  grey: {
    100: "#f8f9fa",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  gradients: {
    primary: {
      main: "#00897b",
      state: "#00bfa5",
    },

    secondary: {
      main: "#627594",
      state: "#a8b8d8",
    },

    info: {
      main: "#00897b",
      state: "#1de9b6",
    },

    success: {
      main: "#17ad37",
      state: "#98ec2d",
    },

    warning: {
      main: "#f53939",
      state: "#fbcf33",
    },

    error: {
      main: "#ea0606",
      state: "#ff667c",
    },

    light: {
      main: "#ced4da",
      state: "#ebeff4",
    },

    dark: {
      main: "#141727",
      state: "#3a416f",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  alertColors: {
    primary: {
      main: "#7928ca",
      state: "#d6006c",
      border: "#efb6e2",
    },

    secondary: {
      main: "#627594",
      state: "#8ca1cb",
      border: "#dadee6",
    },

    info: {
      main: "#2152ff",
      state: "#02c6f3",
      border: "#b9ecf8",
    },

    success: {
      main: "#17ad37",
      state: "#84dc14",
      border: "#daf3b9",
    },

    warning: {
      main: "#f53939",
      state: "#fac60b",
      border: "#fef1c2",
    },

    error: {
      main: "#ea0606",
      state: "#ff3d59",
      border: "#f9b4b4",
    },

    light: {
      main: "#ced4da",
      state: "#d1dae6",
      border: "#f8f9fa",
    },

    dark: {
      main: "#141727",
      state: "#2c3154",
      border: "#c2c8d1",
    },
  },

  badgeColors: {
    primary: {
      background: "#b2dfdb",
      text: "#00695c",
    },

    secondary: {
      background: "#e4e8ed",
      text: "#5974a2",
    },

    info: {
      background: "#b2dfdb",
      text: "#00695c",
    },

    success: {
      background: "#cdf59b",
      text: "#67b108",
    },

    warning: {
      background: "#fef5d3",
      text: "#fbc400",
    },

    error: {
      background: "#fc9797",
      text: "#bd0000",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  inputColors: {
    borderColor: { main: "#d2d6da", focus: "#00bfa5" },
    boxShadow: "#80cbc4",
    error: "#fd5c70",
    success: "#66d432",
  },

  sliderColors: {
    thumb: { borderColor: "#d9d9d9" },
  },

  circleSliderColors: {
    background: "#d3d3d3",
  },

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },
};

// Professional dark theme color palette
const darkColors = {
  background: {
    default: "#0f1419", // Dark background
    paper: "#1a2332", // Elevated surface - lighter than default for better card visibility
  },

  text: {
    main: "#d0dae6", // Bright gray for better text readability
    focus: "#ffffff", // Pure white for focus states
    primary: "#f0f4f8", // Near white for primary text - high contrast
    secondary: "#e8edf4", // Very bright gray for secondary text (card titles) - improved contrast
    disabled: "#6b7a90", // Dimmed gray for disabled
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#141414",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#00bfa5",
    focus: "#1de9b6",
  },

  secondary: {
    main: "#3b82f6", // Blue
    focus: "#60a5fa", // Lighter blue for hover
  },

  info: {
    main: "#00bfa5",
    focus: "#1de9b6",
  },

  success: {
    main: "#4caf50", // Material green — aligned with MkDocs accent
    focus: "#81c784", // Lighter green for hover
  },

  warning: {
    main: "#f59e0b", // Amber
    focus: "#fbbf24", // Lighter amber
  },

  error: {
    main: "#ef4444", // Red
    focus: "#f87171", // Lighter red
  },

  light: {
    main: "#3a4a5e", // Dark gray for light backgrounds in dark mode
  },

  dark: {
    main: "#f0f4f8", // Near white (for text that was "dark" in light mode)
    focus: "#ffffff", // Pure white
  },

  grey: {
    100: "#2a3544", // Darkest - for subtle backgrounds
    200: "#3a4a5e", // Dark - for borders/dividers
    300: "#4a5a6e", // Medium-dark
    400: "#6b7a90", // Medium - for secondary UI
    500: "#8a98ad", // Medium-light - for tertiary text
    600: "#b8c5d6", // Light - for body text
    700: "#d0dae6", // Lighter - for emphasis
    800: "#e8edf4", // Very light - for headers
    900: "#f8fafc", // Almost white - for maximum contrast
  },

  gradients: {
    primary: {
      main: "#00897b",
      state: "#00bfa5",
    },

    secondary: {
      main: "#1d4ed8",
      state: "#60a5fa",
    },

    info: {
      main: "#00897b",
      state: "#00bfa5",
    },

    success: {
      main: "#2e7d32", // Green 800 — deep green (MkDocs diagram borders)
      state: "#66bb6a", // Green 400 — bright green highlight
    },

    warning: {
      main: "#f59e0b",
      state: "#fbbf24",
    },

    error: {
      main: "#ef4444",
      state: "#f87171",
    },

    light: {
      main: "#4b5563",
      state: "#6b7280",
    },

    dark: {
      main: "#1f2937",
      state: "#374151",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#4267B2",
      dark: "#365899",
    },

    twitter: {
      main: "#1DA1F2",
      dark: "#1a91da",
    },

    instagram: {
      main: "#E1306C",
      dark: "#C13584",
    },

    linkedin: {
      main: "#0A66C2",
      dark: "#004182",
    },

    pinterest: {
      main: "#E60023",
      dark: "#bd081c",
    },

    youtube: {
      main: "#FF0000",
      dark: "#cc0000",
    },

    vimeo: {
      main: "#1AB7EA",
      dark: "#00adef",
    },

    slack: {
      main: "#4A154B",
      dark: "#36123b",
    },

    dribbble: {
      main: "#EA4C89",
      dark: "#d63b74",
    },

    github: {
      main: "#333333",
      dark: "#24292e",
    },

    reddit: {
      main: "#FF4500",
      dark: "#d93b00",
    },

    tumblr: {
      main: "#35465C",
      dark: "#2c3a4a",
    },
  },

  alertColors: {
    primary: {
      main: "#ec4899",
      state: "#f472b6",
      border: "#831843",
    },

    secondary: {
      main: "#3b82f6",
      state: "#60a5fa",
      border: "#1e40af",
    },

    info: {
      main: "#06b6d4",
      state: "#22d3ee",
      border: "#164e63",
    },

    success: {
      main: "#10b981",
      state: "#34d399",
      border: "#065f46",
    },

    warning: {
      main: "#f59e0b",
      state: "#fbbf24",
      border: "#92400e",
    },

    error: {
      main: "#ef4444",
      state: "#f87171",
      border: "#991b1b",
    },

    light: {
      main: "#6b7280",
      state: "#9ca3af",
      border: "#374151",
    },

    dark: {
      main: "#1f2937",
      state: "#374151",
      border: "#6b7280",
    },
  },

  badgeColors: {
    primary: {
      background: "#00453d",
      text: "#6ee7b7",
    },

    secondary: {
      background: "#1e40af",
      text: "#93c5fd",
    },

    info: {
      background: "#164e63",
      text: "#67e8f9",
    },

    success: {
      background: "#065f46",
      text: "#6ee7b7",
    },

    warning: {
      background: "#92400e",
      text: "#fcd34d",
    },

    error: {
      background: "#991b1b",
      text: "#fca5a5",
    },

    light: {
      background: "#374151",
      text: "#e5e7eb",
    },

    dark: {
      background: "#6b7280",
      text: "#f9fafb",
    },
  },

  inputColors: {
    borderColor: { main: "#374151", focus: "#00bfa5" }, // Teal focus ring
    boxShadow: "#80cbc4", // Teal glow (material teal 200)
    error: "#ef4444",
    success: "#4caf50",
  },

  sliderColors: {
    thumb: { borderColor: "#4b5563" },
  },

  circleSliderColors: {
    background: "#374151",
  },

  tabs: {
    indicator: { boxShadow: "#374151" },
  },
};

export default {
  light: lightColors,
  dark: darkColors,
};
